import { LoadingSpinner } from "components/LoadingSpinner";
import { useMemo } from "react";
import { useGetPayersByProvidersQuery } from "store/queries/payers";
import styled from "styled-components";

/**
 * CLE-2658 — the payers the selected providers already have live, offered as one-click adds
 * under a payer field.
 *
 * Both forms this appears on (new fee schedule, new payer rule) pick from every payer in the
 * system — 91 of them in prod against the 15 a given hospital actually has. Ani's workflow was
 * to open the provider's library in another tab, read off the live payers, come back and type
 * them in one at a time. This puts that list where the choice is made.
 */

const Container = styled.div`
  margin-top: 0.5rem;
`;

const PayerButton = styled.button`
  background: var(--surface-0);
  border: 1px solid var(--primary-500);
  border-radius: 16px;
  color: var(--primary-500);
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  padding: 0.15rem 0.6rem;

  &:hover {
    background: var(--primary-500);
    color: var(--surface-0);
  }

  &:disabled {
    background: var(--surface-200);
    border-color: var(--surface-300);
    color: var(--gray-600);
    cursor: default;
  }
`;

const AddAllButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-500);
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
`;

export interface ILivePayer {
  id: number;
  name: string;
}

interface ILivePayersProps {
  /** Providers currently selected on the form. No providers, nothing to show. */
  providerIds: string[];
  /** Payer ids already on the form — those render as added rather than clickable. */
  selectedPayerIds: number[];
  /**
   * Always a batch, even for a single chip. "Add all" would otherwise fire one call per payer
   * in a single tick and each caller would have to defend against its own stale state.
   */
  onAdd: (payers: ILivePayer[]) => void;
}

export function LivePayers({
  providerIds,
  selectedPayerIds,
  onAdd,
}: ILivePayersProps) {
  // Sorted so the cache key doesn't depend on the order the providers happened to be picked in.
  const sortedProviderIds = useMemo(
    () => [...providerIds].sort(),
    [providerIds]
  );

  const { data, isFetching } = useGetPayersByProvidersQuery(sortedProviderIds, {
    skip: sortedProviderIds.length === 0,
  });

  const payers: ILivePayer[] = useMemo(
    () =>
      (data?.data ?? []).map((payer: any) => ({
        id: payer.id,
        name: payer.name,
      })),
    [data]
  );

  const notYetAdded = payers.filter(
    (payer) => !selectedPayerIds.includes(payer.id)
  );

  if (sortedProviderIds.length === 0) return null;

  if (isFetching) {
    return (
      <Container>
        <LoadingSpinner customStyle={{ width: "16px", height: "16px" }} />
      </Container>
    );
  }

  if (payers.length === 0) {
    return (
      <Container>
        <span className="text-sm text-gray-600">
          No live payers for the selected provider
          {sortedProviderIds.length > 1 ? "s" : ""} yet.
        </span>
      </Container>
    );
  }

  return (
    <Container data-testid="live-payers">
      <div className="flex align-items-center gap-2 mb-2">
        <span className="text-sm text-gray-700">
          Live payers for the selected provider
          {sortedProviderIds.length > 1 ? "s" : ""}:
        </span>
        {notYetAdded.length > 1 && (
          <AddAllButton type="button" onClick={() => onAdd(notYetAdded)}>
            Add all
          </AddAllButton>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {payers.map((payer) => {
          const alreadyAdded = selectedPayerIds.includes(payer.id);

          return (
            <PayerButton
              key={payer.id}
              type="button"
              disabled={alreadyAdded}
              onClick={() => onAdd([payer])}
            >
              {alreadyAdded && <i className="pi pi-check mr-1 text-xs" />}
              {payer.name}
            </PayerButton>
          );
        })}
      </div>
    </Container>
  );
}
